import { useEffect, useState, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Translate } from "../../Enums/Tranlate";
import "./style.scss";

const Pagination = ({
	setData,
	service,
	shouldUpdate,
	isDeleted,
	setHasData,
	setLoading,
	type,
	search,
	param,
	setTotal,
}) => {
	const [totalPages, setTotalPages] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState("25");
	const [totalCount, setTotalCount] = useState(0);
	const lang = useSelector((state) => state.auth.lang);
	const prevFiltersRef = useRef({ shouldUpdate, search, isDeleted });
	const pageSizeOptions = [
		{ value: "25", label: "25" },
		{ value: "50", label: "50" },
		{ value: "75", label: "75" },
		{ value: "100", label: "100" },
		{ value: "all", label: Translate[lang]?.all ?? "All" },
	];

	useEffect(() => {
		const filtersChanged =
			prevFiltersRef.current.shouldUpdate !== shouldUpdate ||
			prevFiltersRef.current.search !== search ||
			prevFiltersRef.current.isDeleted !== isDeleted;

		prevFiltersRef.current = { shouldUpdate, search, isDeleted };

		// If filters/search changed and we were not on page 1, reset to page 1 and wait for next render
		if (filtersChanged && page !== 1) {
			setPage(1);
			return;
		}

		setLoading(true);
		const limitNumber =
			pageSize === "all" ? totalCount || 1000 : parseInt(pageSize) || 25;
		let params = {
			page: page,
			limit: limitNumber,
			...param,
		};
		if (!!type) params["type"] = type;
		if (!!search) params["search"] = search;

		service
			?.getList({ ...params })
			.then((res) => {
				if (res?.status === 200) {
					let response = res?.data?.data;
					const metaTotal = response?.meta?.total ?? 0;
					const backendTotalPages = response?.meta?.totalPages;
					setTotal?.(metaTotal);
					setTotalCount(metaTotal);
					if (metaTotal > 0) {
						let total =
							backendTotalPages !== undefined && backendTotalPages !== null
								? backendTotalPages
								: pageSize === "all"
								? 1
								: Math.ceil(metaTotal / limitNumber);
						setTotalPages(total);
						setData([...(response?.data ?? [])]);
						setHasData(1);
					} else {
						setTotalPages(0);
						setData([]);
						setHasData(0);
					}
				} else {
					setTotalPages(0);
					setData([]);
					setHasData(0);
				}
				setLoading(false);
			})
			.catch(() => {
				setTotalPages(0);
				setData([]);
				setHasData(0);
				setLoading(false);
			});

		window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
	}, [page, shouldUpdate, search, pageSize, isDeleted]);

	const handlePageSizeChange = (value) => {
		setPage(1);
		setPageSize(value);
	};

	const getPageNumbers = () => {
		if (!totalPages || totalPages <= 0) return [];
		const current = parseInt(page) || 1;
		const delta = 1; // pages to show on each side of the current page
		const range = [];

		range.push(1);
		for (let i = current - delta; i <= current + delta; i++) {
			if (i > 1 && i < totalPages) range.push(i);
		}
		if (totalPages > 1) range.push(totalPages);

		// de-duplicate and sort, then insert ellipsis markers
		const unique = [...new Set(range)].sort((a, b) => a - b);
		const withDots = [];
		unique.forEach((num, idx) => {
			if (idx > 0 && num - unique[idx - 1] > 1) withDots.push("...");
			withDots.push(num);
		});
		return withDots;
	};

	if (!totalCount || totalCount <= 0 || !totalPages || totalPages <= 0) {
		return null;
	}

	if (totalPages <= 1 && pageSize !== "all") {
		return null;
	}

	return (
		<Row className="pagination mt-3 px-2">
				<Col md={12} className="text-center">
					<div className="filter-pagination d-flex justify-content-between mt-3">
						<button
							className="previous-button"
							onClick={() => {
								setPage((prev) => parseInt(prev) - 1);
							}}
							disabled={parseInt(page) === 1}>
							{lang === "en" ? (
								<i className="la la-arrow-left"></i>
							) : (
								<i className="la la-arrow-right"></i>
							)}{" "}
							{Translate[lang]?.previous}
						</button>
						<div className="d-flex" style={{gap: "16px"}}>

						<select
							className="form-control"
							style={{
								width: "auto",
								height: "38px",
								cursor: "pointer",
							}}
							value={pageSize}
							onChange={(e) => handlePageSizeChange(e.target.value)}>
							{pageSizeOptions?.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
						<div className="d-flex align-items-center" style={{ gap: "5px" }}>
							{getPageNumbers()?.map((num, index) => {
								if (num === "...") {
									return (
										<p
											key={`dots-${index}`}
											style={{
												padding: "5px 10px",
												margin: "0",
												color: "#999",
											}}>
											...
										</p>
									);
								}
								const isActive = parseInt(page) === parseInt(num);
								return (
									<p
										key={num}
										onClick={() => {
											setPage(num);
										}}
										style={{
											padding: "5px 12px",
											margin: "0",
											cursor: "pointer",
											borderRadius: "6px",
											fontWeight: isActive ? "600" : "400",
											color: isActive ? "#fff" : "",
											backgroundColor: isActive ? "var(--primary)" : "",
										}}>
										{num}
									</p>
								);
							})}
						</div>
						</div>
						
						<button
							className="next-button"
							onClick={() => {
								setPage((prev) => parseInt(prev) + 1);
							}}
							disabled={parseInt(page) === totalPages}>
							{Translate[lang]?.next}{" "}
							{lang === "en" ? (
								<i className="la la-arrow-right"></i>
							) : (
								<i className="la la-arrow-left"></i>
							)}
						</button>
					</div>
				</Col>
			</Row>
		);
};

export default Pagination;
